import { getTheme, type ThemeName } from './commands';

export interface SimColors {
  empty: string;
  wall: string;
  nest: string;
  food: string;
  foodRgb: [number, number, number];
  emptyRgb: [number, number, number];
  ant: string;
  antCarrying: string;
  antSelected: string;
  antDimmed: string;
  pheromone: [number, number, number][];
  pheromoneLabels: string[];
  gridLine: string;
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
}

function getCssVar(name: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
}

/**
 * Colorblind-safe pheromone channel colors (Okabe-Ito inspired).
 * Designed with both hue AND luminance variation so channels remain
 * distinguishable under protanopia, deuteranopia, and tritanopia.
 */
const PHEROMONE_COLORS: [number, number, number][] = [
  [240, 130, 40],  // CH_RED    → warm orange
  [80, 160, 255],  // CH_BLUE   → sky blue
  [50, 200, 140],  // CH_GREEN  → bluish-green (shifted from pure green)
  [230, 190, 50],  // CH_YELLOW → gold
];

const PHEROMONE_LABELS = ['R', 'B', 'G', 'Y'];

interface EntityColors {
  food: string;
  nest: string;
  antCarrying: string;
  antSelected: string;
  antDimmed: string;
}

const ENTITY: Record<ThemeName, EntityColors> = {
  default: {
    food: '#4a9eff',
    nest: '#e0e0e0',
    antCarrying: '#ffd866',
    antSelected: '#ffffff',
    antDimmed: 'rgba(80,80,80,0.3)',
  },
  paper: {
    food: '#2563eb',
    nest: '#9a9080',
    antCarrying: '#c2610c',
    antSelected: '#1a1a1a',
    antDimmed: 'rgba(160,150,130,0.4)',
  },
  mono: {
    food: '#8abbff',
    nest: '#666666',
    antCarrying: '#ffffff',
    antSelected: '#ffd84d',
    antDimmed: 'rgba(100,100,100,0.3)',
  },
  pacman: {
    food: '#ffdd00',
    nest: '#ffffff',
    antCarrying: '#ffb8ff',
    antSelected: '#ffffff',
    antDimmed: 'rgba(60,60,100,0.3)',
  },
  sepia: {
    food: '#6a98c8',
    nest: '#c8b898',
    antCarrying: '#f0cc70',
    antSelected: '#f5e6d0',
    antDimmed: 'rgba(100,80,50,0.3)',
  },
  bauhaus: {
    food: '#0055cc',
    nest: '#999999',
    antCarrying: '#ff8800',
    antSelected: '#1a1a1a',
    antDimmed: 'rgba(160,155,145,0.4)',
  },
};

/**
 * Resolve the current theme's simulation color palette.
 * Structural colors (empty, wall) come from live CSS variables;
 * entity colors (food, ant, carrying) are per-theme constants
 * chosen for accessibility and colorblind safety.
 */
export function getSimColors(): SimColors {
  const theme = getTheme();
  const entity = ENTITY[theme];
  const empty = getCssVar('--color-panel') || '#0a110e';
  const wall = getCssVar('--color-stroke') || '#1a2e26';
  const ant = getCssVar('--color-primary') || '#3cd8a8';

  return {
    empty,
    wall,
    nest: entity.nest,
    food: entity.food,
    foodRgb: hexToRgb(entity.food),
    emptyRgb: hexToRgb(empty),
    ant,
    antCarrying: entity.antCarrying,
    antSelected: entity.antSelected,
    antDimmed: entity.antDimmed,
    pheromone: PHEROMONE_COLORS,
    pheromoneLabels: PHEROMONE_LABELS,
    gridLine: `${wall}20`,
  };
}

/** Interpolate food color by density (0–1 range after clamping). */
export function foodColor(colors: SimColors, foodAmount: number): string {
  const t = Math.min(1, foodAmount / 4);
  const base = 0.2;
  const s = base + t * (1 - base);
  const [er, eg, eb] = colors.emptyRgb;
  const [fr, fg, fb] = colors.foodRgb;
  const r = Math.round(er + s * (fr - er));
  const g = Math.round(eg + s * (fg - eg));
  const b = Math.round(eb + s * (fb - eb));
  return `rgb(${r},${g},${b})`;
}
